"use client";

import { Badge } from "@/components/ui/badge";
import Image from "next/image";
import React from "react";
import { FaGithub } from "react-icons/fa";
import { GoLinkExternal } from "react-icons/go";
import { MdDoNotDisturbAlt } from "react-icons/md";
import InfoTag from "./InfoTag";
import ExternalLink from "./ExternalLink";
import { useTheme } from "next-themes";
import { useTranslations } from "next-intl";
import { TPorjects } from "./Projects";
import SpotlightCard from "@/animations/SpotlightCard";
import { BoxReveal } from "@/components/magicui/box-reveal";
import { BOX_REVEAL_DELAY, MAIN_COLOR } from "@/utils/constants";

const ProjectCard = ({
  title,
  description,
  image,
  techs = [],
  githubLink,
  liveDemoLink,
  projectRelated = "frontend",
}: Partial<TPorjects>) => {
  const { resolvedTheme } = useTheme();
  const t = useTranslations();

  return (
    <BoxReveal fullHeight boxColor={MAIN_COLOR} duration={BOX_REVEAL_DELAY}>
      <SpotlightCard
        className="shadow-xl !h-full rounded-[5px] flex flex-col justify-between border !border-gray-300 dark:!border-input !p-3 bg-white dark:bg-input/30 dark:shadow-none dark:backdrop-blur-sm"
        spotlightColor={
          resolvedTheme === "dark"
            ? "rgba(255, 255, 255, 0.15)"
            : "rgba(0, 0, 0, 0.15)"
        }
      >
        <div className="relative w-full aspect-[16/9] rounded-[5px] overflow-hidden">
          <Image
            src={image ?? ""}
            alt={title ?? ""}
            fill
            className="object-cover rounded-[5px]"
            sizes="(max-width: 768px) 100vw, 33vw"
            loading="lazy"
          />
        </div>

        <div className="flex flex-col gap-2 mt-2">
          <h2 className="text-xl font-bold">{title}</h2>

          <div className="flex gap-2 flex-wrap items-center">
            {[projectRelated, ...techs].map((tag) => (
              <Badge
                key={tag}
                variant={"outline"}
                className="rounded-[5px] dark:bg-input/30 dark:text-gray-400  capitalize py-1 text-xs bg-light text-center whitespace-normal"
              >
                <abbr>#{tag}</abbr>
              </Badge>
            ))}
          </div>

          <p className="text-sm leading-[1.7]">{description}</p>
        </div>

        <div className="flex items-center gap-2 mt-4">
          {getFooter(t, githubLink, liveDemoLink)}
        </div>
      </SpotlightCard>
    </BoxReveal>
  );
};

const getFooter = (
  t: (key: string) => string,
  githubLink?: string,
  liveDemoLink?: string
): React.ReactNode => {
  if (!githubLink && !liveDemoLink) {
    return (
      <p className="font-semibold text-sm text-primary dark:bg-input/30 p-1 dark:text-gray-400 rounded-[5px] text-center whitespace-normal w-full">
        {t("Access to this project is restricted under NDA")}
      </p>
    );
  }

  if (!githubLink && liveDemoLink) {
    return (
      <div className="flex justify-between w-full">
        <InfoTag
          icon={<MdDoNotDisturbAlt className="w-6 h-6" />}
          label={t("Private repo")}
        />
        <ExternalLink
          href={liveDemoLink}
          icon={<GoLinkExternal className="w-6 h-6" />}
          label="Live Demo"
        />
      </div>
    );
  }

  if (githubLink && !liveDemoLink) {
    return (
      <div className="flex justify-between w-full">
        <ExternalLink
          href={githubLink}
          icon={<FaGithub className="w-6 h-6" />}
          label="Github Repo"
        />
        <InfoTag
          icon={<MdDoNotDisturbAlt className="w-6 h-6" />}
          label={t("Unavailable")}
        />
      </div>
    );
  }

  return (
    <div className="flex justify-between w-full">
      {githubLink && liveDemoLink && (
        <>
          <ExternalLink
            href={githubLink}
            icon={<FaGithub className="w-6 h-6" />}
            label="Github Repo"
          />
          <ExternalLink
            href={liveDemoLink}
            icon={<GoLinkExternal className="w-6 h-6" />}
            label="Live Demo"
          />
        </>
      )}
    </div>
  );
};

export default ProjectCard;
